<template>
  <div>
    <md-dialog :md-closed="closeDialog" :md-opened="openDialog" class="" :md-close-on-esc="false"
               :md-click-outside-to-close="false"
               :md-active.sync="options.showUserForm">
      <md-dialog-title class="ml-30 mt-20">{{
          currentFormUser ? options.Lang.Benutzer_bearbeiten : options.Lang.Neuer_Benutzer
        }}
      </md-dialog-title>
      <md-dialog-content style="padding: 20px !important;">
        <div class="pt-30">
          <form novalidate @submit.prevent="validate">
            <!-- Main layout: Image left, all inputs right -->
            <div style="display: flex; gap: 100px; align-items: flex-start;">
              <!-- Image column: round picture = upload target (click / drop) -->
              <div style="flex: 0 0 auto; margin-left: 100px;">
                <AvatarUpload
                    v-model="form.pic"
                    :disabled="sending || !options.userFormEditable"
                    :label="options.Lang.Account_Logo"
                    :upload-label="options.Lang.user_pic_upload"
                    :change-label="options.Lang.user_pic_change"
                    :remove-label="options.Lang.user_pic_remove"
                    :drop-label="options.Lang.user_pic_drop"
                    :hint="options.Lang.user_pic_hint"
                    @error="onPicError"
                />
              </div>

              <!-- Inputs column: 2 cols x 3 rows -->
              <div style="flex: 1; min-width: 0;">
                <div class="md-layout md-gutter">
                  <!-- Row 1: Vorname / Nachname -->
                  <div class="md-layout-item md-size-50 md-small-size-100">
                    <md-field class="md-focused" :class="getValidationClass('firstname')">
                      <label for="firstname">{{options.Lang.Vorname}}<span class="md-required">*</span></label>
                      <md-input tabindex="1" name="firstname" :placeholder=options.Lang.Vorname id="firstname" v-model="form.firstname"
                                :disabled="sending || !options.userFormEditable"/>
                      <span class="md-error" v-if="!$v.form.firstname.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-50 md-small-size-100">
                    <md-field class="md-focused" :class="getValidationClass('lastname')">
                      <label for="lastname">{{options.Lang.Nachname}}<span class="md-required">*</span></label>
                      <md-input tabindex="2" name="lastname" :placeholder=options.Lang.Nachname id="lastname" v-model="form.lastname"
                                :disabled="sending || !options.userFormEditable"/>
                      <span class="md-error" v-if="!$v.form.lastname.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>

                  <!-- Row 2: Firma / Rolle -->
                  <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                    <md-field class="md-focused" :class="getValidationClass('company')">
                      <label for="company">{{options.Lang.Firma}}<span class="md-required">*</span></label>
                      <md-input tabindex="3" name="company" :placeholder=options.Lang.Firma id="company" autocomplete="company" v-model="form.company"
                                :disabled="sending || !options.userFormEditable"/>
                      <span class="md-error" v-if="!$v.form.company.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                    <md-field class="md-focused" :class="getValidationClass('role')">
                      <label for="role">{{options.Lang.Rolle}}<span class="md-required">*</span></label>
                      <md-select tabindex="4" name="role" id="role" v-model="form.role" md-dense
                                 :disabled="sending || !options.userFormEditable">
                        <md-option value="0">{{options.Lang.Mitarbeiter}}</md-option>
                        <md-option value="1">{{options.Lang.Admin}}</md-option>
                        <md-option v-if="options.user.supersuper == 1 || options.user.supersuper == true" value="11">{{options.Lang.Superadmin}}</md-option>
                      </md-select>
                      <span class="md-error" v-if="!$v.form.role.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>

                  <!-- Row 3: E-Mail / Telefon -->
                  <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                    <md-field class="md-focused" :class="[getValidationClass('username'), { 'email-locked': emailLocked }]">
                      <label for="user_email">E-Mail<span class="md-required">*</span></label>
                      <!-- Not named/marked as a login field: password managers must not autofill the admin's own login here -->
                      <md-input tabindex="5" name="user_email" placeholder="E-Mail" id="user_email" autocomplete="off"
                                data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
                                v-model="form.username"
                                :readonly="emailLocked"
                                :disabled="sending || !options.userFormEditable"/>
                      <md-icon v-if="currentFormUser" class="email-lock-icon"
                               :title="emailLocked ? options.Lang.user_email_unlock : ''"
                               @click.native="toggleEmailLock">{{ emailLocked ? 'lock' : 'lock_open' }}</md-icon>
                      <span class="md-error" v-if="!$v.form.username.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>
                  <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                    <md-field class="md-focused" :class="getValidationClass('telefon')">
                      <label for="telefon">{{options.Lang.Telefon}}<span class="md-required">*</span></label>
                      <md-input tabindex="6" name="telefon" :placeholder=options.Lang.Telefon id="telefon" v-model="form.telefon"
                                autocomplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true" data-form-type="other"
                                :disabled="sending || !options.userFormEditable"/>
                      <span class="md-error" v-if="!$v.form.telefon.required">{{options.Lang.Bitte_ausfüllen}}</span>
                    </md-field>
                  </div>

                  <!-- New user: Password fields -->
                  <template v-if="!currentFormUser">
                    <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                      <md-field class="md-focused" :class="showPwError ? 'md-invalid' : ''">
                        <label for="password">{{options.Lang.Passwort}}</label>
                        <md-input tabindex="7" name="password" autocomplete="new-password" placeholder="*********" type="password"
                                  id="password" v-model="form.password" :disabled="sending"/>
                        <span class="md-error" v-if="showPwError">{{ options.Lang.Passwörter_müssen_übereinstimmen }}</span>
                      </md-field>
                    </div>
                    <div class="md-layout-item md-size-50 md-small-size-100 pt-30">
                      <md-field class="md-focused" :class="showPwError ? 'md-invalid' : ''">
                        <label for="passwordRepeat">{{ options.Lang.neues_Passwort_wiederholen }}</label>
                        <md-input tabindex="8" name="passwordRepeat" autocomplete="new-password" placeholder="*********" type="password"
                                  id="passwordRepeat" v-model="form.passwordRepeat" :disabled="sending"/>
                        <span class="md-error" v-if="showPwError">{{ options.Lang.Passwörter_müssen_übereinstimmen }}</span>
                      </md-field>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Brandings full width -->
            <div class="pt-30">
              <label>{{options.Lang.Branding_Zulage}}</label>
              <div style="display: flex; flex-wrap: wrap; gap: 0 20px;">
                <div v-for="brand in brands" :key="brand.id">
                  <md-switch tabindex="-1" class="md-primary" v-model="brandingsAllowed[brand.id]" @change="toggleBrand(brand.id)">{{brand.adresse}}</md-switch>
                </div>
              </div>
            </div>
          </form>
        </div>
      </md-dialog-content>
      <md-dialog-actions class="mt-20 pRight24">
        <div class="p-20 width100">
          <div class="right mb--10">
            <md-switch tabindex="-1" class="mt-0 md-primary" v-model="form.status" :value="1">{{options.Lang.Aktiver_Benutzer}}</md-switch>
            <md-switch tabindex="-1" v-if="user" class="md-primary" v-model="form.trips_isolated" :value="1">{{options.Lang.trips_isolated || 'Isolate Trips'}}</md-switch>
          </div>
          <div class="userButtonContainer">
            <md-button v-if="currentFormUser" tabindex="-1" class="md-raised secondary" @click="rmvUser()"><strong>{{options.Lang.Benutzer_löschen}}</strong></md-button>
            <div :class="[ currentFormUser ? 'dFlexCen' : 'dFlexEnd' ]">
              <md-button tabindex="-1" v-if="currentFormUser && (parseInt(options.user.role) !== 0 || parseInt(options.user.supersuper) === 1)" class="md-raised" :disabled="!userTotpEnabled" :style="userTotpEnabled ? 'background: #e65100; color: white;' : ''" @click="show2FAConfirm = true">{{ options.Lang['2fa_admin_disable_button'] || 'Disable 2FA' }}</md-button>
              <md-button tabindex="-1" v-if="currentFormUser && options.userFormEditable" class="md-raised" style="background: #1565c0; color: white;" @click="showPasswordModal = true">{{ options.Lang.Passwort_ändern }}</md-button>
              <md-button  tabindex="-1" v-if="options.userFormEditable" class="md-raised md-primary" @click="save">{{ options.Lang.Speichern }}</md-button>
              <md-button  tabindex="-1" v-else class="md-raised md-primary" @click="openEdit"><strong>{{options.Lang.Benutzer_bearbeiten}}</strong></md-button>
              <md-button  tabindex="-1" class="md-raised secondary" @click="closeDialog"><strong>{{options.Lang.Abbrechen}}</strong></md-button>
            </div>
          </div>
        </div>
      </md-dialog-actions>
      <Error class="errorDialog" v-if="options.showError && options.user.loggedIn" :options="options"></Error>
      <Success :options="options"></Success>
      <div v-if="sending" class="form-user-loader-overlay">
        <div class="form-user-spinner"></div>
      </div>
    </md-dialog>

    <!-- Password Change Modal (outside main dialog) -->
    <md-dialog :md-active.sync="showPasswordModal" :md-close-on-esc="true" :md-click-outside-to-close="true" class="password-change-dialog">
      <md-dialog-title class="ml-30 mt-20">{{ options.Lang.Passwort_ändern }}</md-dialog-title>
      <md-dialog-content style="padding: 24px !important;">
        <div style="padding: 0 16px;">
          <div>
            <md-field class="md-focused" :class="showPwError ? 'md-invalid' : ''">
              <label for="pwModalPassword">{{ options.Lang.Passwort || 'Neues Passwort' }}</label>
              <md-input name="pwModalPassword" autocomplete="new-password" placeholder="*********" type="password"
                        id="pwModalPassword"
                        v-model="pwModal.password" />
              <span class="md-error" v-if="showPwError">{{ options.Lang.Passwörter_müssen_übereinstimmen }}</span>
            </md-field>
          </div>
          <div style="padding-top: 40px;">
            <md-field class="md-focused" :class="showPwError ? 'md-invalid' : ''">
              <label for="pwModalPasswordRepeat">{{ options.Lang.neues_Passwort_wiederholen || 'Passwort wiederholen' }}</label>
              <md-input name="pwModalPasswordRepeat" autocomplete="new-password" placeholder="*********" type="password"
                        id="pwModalPasswordRepeat"
                        v-model="pwModal.passwordRepeat" />
              <span class="md-error" v-if="showPwError">{{ options.Lang.Passwörter_müssen_übereinstimmen }}</span>
            </md-field>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-raised md-primary" @click="savePassword">{{ options.Lang.Speichern }}</md-button>
        <md-button class="md-raised secondary" @click="closePasswordModal"><strong>{{ options.Lang.Abbrechen }}</strong></md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- 2FA Confirm Dialog (outside main dialog) -->
    <md-dialog :md-active.sync="show2FAConfirm" :md-click-outside-to-close="true" :md-close-on-esc="true" class="confirm-2fa-dialog" :md-backdrop="false">
      <md-dialog-title style="text-align: center; font-weight: bold; padding: 24px 24px 0 !important;">{{ options.Lang['2fa_admin_disable_button'] || 'Disable 2FA' }}</md-dialog-title>

      <md-dialog-content style="text-align: center; font-size: 1rem; padding: 24px 40px !important;">
        <p>{{ options.Lang['2fa_admin_confirm'] || 'Are you sure you want to disable 2FA for this user?' }}</p>
      </md-dialog-content>

      <md-dialog-actions style="justify-content: center; padding: 0 24px 24px !important;">
        <md-button class="md-raised md-primary" @click="show2FAConfirm = false">{{ options.Lang['Abbrechen'] || 'Cancel' }}</md-button>
        <md-button class="md-raised" style="background: #e65100; color: white;" @click="confirmReset2FA">{{ options.Lang['confirm'] || 'Confirm' }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import {validationMixin} from 'vuelidate'
import {
  required,
} from 'vuelidate/lib/validators'
import AvatarUpload from '../parts/AvatarUpload.vue'
import Success from '../../components/dialogs/Success.vue'
import Error from '../../components/dialogs/Error.vue'

export default {
  name: 'formTemplate',
  mixins: [validationMixin],
  components: {
    AvatarUpload, Success, Error
  },
  props: {
    options: {
      type: Object,
      required: true
    },
    currentFormUser: {
      type: Object
    },
  },
  data: () => ({
    user: {},
    password_old: null,
    showPwError: false,
    form: {
      firstname: null,
      lastname: null,
      username: null,
      company: null,
      password: null,
      passwordRepeat: null,
      role: 0,
      telefon: null,
      trips_isolated:null,
      pic: null,
    id: null,
    changePassword: true,
    status: 1,
    brandings: null
  },
  userSaved: false,
  sending: false,
  emailLocked: false,
  userTotpEnabled: false,
  show2FAConfirm: false,
  showPasswordModal: false,
  pwModal: { password: null, passwordRepeat: null },
  brands:[],
  brandingsAllowed:{},
  brandingsToForm : [],
  render:0
}),
validations: {
  form: {
    username: {
      required,
    },
    telefon: {
      required,
    },
    firstname: {
      required,
    },
    lastname: {
      required
    },
    role: {
      required
    },
    company: {
      required
    },
    pic: {},
    brandings:{
      required
    }
  }
},
async created() {
  await this.getBrandings()

  },

mounted() {
  this.user = this.currentFormUser;
  if(this?.user?.supersuper == 1){
    this.form.role = "11"
  }
},
  watch: {
    currentFormUser: {
      handler(u) {
        this.user = u || null

        this.form = {
          id: u?.id ?? null,
          username: u?.username ?? null,
          firstname: u?.firstname ?? null,
          lastname: u?.lastname ?? null,
          telefon: u?.telefon ?? null,
          status: u?.status ?? null,
          trips_isolated: u?.trips_isolated ?? null,
          pic: u?.pic ?? null,
          role: u?.role ?? null,
          company: u?.company ?? null,
          password: u?.password ?? null,
          passwordRepeat: u?.password ?? null,
          changePassword: !u || !u.id,
          brandings: u?.brandings ? JSON.parse(u.brandings) : null
        }

        // Existing user: login e-mail starts locked, unlocked explicitly via the lock icon
        this.emailLocked = !!(u && u.id)
        this.password_old = u?.password ?? null
        this.brandingsToForm = u?.brandings ? JSON.parse(u.brandings) : null
        this.userTotpEnabled = parseInt(u?.totp_enabled ?? 0) === 1
      },
      immediate: true,
    },
  },
methods: {
  closePasswordModal() {
    this.showPasswordModal = false;
    this.pwModal = { password: null, passwordRepeat: null };
    this.showPwError = false;
  },
  savePassword() {
    let self = this;
    if (!self.pwModal.password || self.pwModal.password === '' || self.pwModal.password !== self.pwModal.passwordRepeat) {
      self.showPwError = true;
      return;
    }
    self.showPwError = false;
    let pwData = Object.assign({}, self.form, {
      changePassword: true,
      password: self.pwModal.password,
      passwordRepeat: self.pwModal.passwordRepeat
    });
    self.doPost({
      userdata: self.options.user,
      data: pwData
    }, 'updateUserById', function (res) {
      if (res.data.status === 'error') {
        self.showRequestError(res);
      } else {
        self.options.showSuccess = true;
        self.options.successMsg = self.options.Lang['Passwort_geändert'] || 'Passwort erfolgreich geändert';
        self.closePasswordModal();
      }
    });
  },
  async confirmReset2FA() {
    this.show2FAConfirm = false;
    let self = this;
    await self.doPost({ userdata: self.options.user, data: { targetUserId: self.form.id } }, 'totp-admin-disable', function (res) {
      if (res.status === 'ok') {
        self.userTotpEnabled = false;
        self.options.showSuccess = true;
        self.options.successMsg = self.options.Lang['2fa_admin_disabled'];
      } else {
        self.userTotpEnabled = true;
        self.showErrorMsg(res.msg || self.options.Lang['2fa_invalid_code']);
      }
    });
  },
  async rmvUser(){
    if(this.form.id == this.options.user.id){
      this.options.showError = true;
    }else{
      let self = this;
      await this.doPost({ data: self.form}, 'rmvUser', function (res) {
      if (res.data.status === 'error') {
        self.showRequestError(res);
      } else {
        self.options.showUserForm = false;
        self.$nextTick(function(){
          window.location = "/users";
        })
      }
    });
    }
  },
  getValidationClass(fieldName) {
    const field = this.$v.form[fieldName]

    if (field) {
      return {
        'md-invalid': field.$invalid && field.$dirty
      }
    }
  },
  // AvatarUpload reports problems as locale keys (invalid type / too big / unreadable)
  onPicError(key) {
    this.showErrorMsg(this.options.Lang[key] || this.options.Lang.user_request_failed);
  },
  clearForm() {
    this.$v.$reset()
    this.form.id = null
    this.form.username = null
    this.form.firstname = null
    this.form.lastname = null
    this.form.telefon = null
    this.form.role = 0
    this.form.status = 1
    this.form.trips_isolated = 0
    this.form.company = null
    this.form.password = null
    this.form.passwordRepeat = null
    this.form.changePassword = false
    this.form.brandings = null
    this.showPwError = false
  },
  trimFormFields() {
    // Trim whitespace from specified fields
    if (this.form.firstname) {
      this.form.firstname = this.form.firstname.trim();
    }
    if (this.form.lastname) {
      this.form.lastname = this.form.lastname.trim();
    }
    if (this.form.company) {
      this.form.company = this.form.company.trim();
    }
    if (this.form.username) {
      this.form.username = this.form.username.trim();
    }
    if (this.form.telefon) {
      this.form.telefon = this.form.telefon.trim();
    }
  },
  save() {
    const self = this;

    // Trim whitespace from form fields before validation
    self.trimFormFields();

    self.form.changePassword = false;
    self.$v.$touch()
    if (self.$v.$invalid) {
      if ((self.form.brandings || []).length !== 0){
        return false;
      }
    }
    if(self.form.username === self.options.user.username){
      self.options.user.pic = self.form.pic;
    }
    self.sending = true;
    this.$nextTick(function() {
      setTimeout(function() {
        const payload = Object.assign({}, self.form);
        let tripsData = null;
        if (self.currentFormUser) {
          // Only send the login e-mail when it was actually changed, so a save never rewrites it by accident
          if (payload.username === self.currentFormUser.username) {
            delete payload.username;
          }
          if (self.form.firstname !== self.currentFormUser.firstname || self.form.lastname !== self.currentFormUser.lastname) {
            tripsData = {
              firstname: self.form.firstname,
              lastname: self.form.lastname,
              firstnameOld: self.currentFormUser.firstname,
              lastnameOld: self.currentFormUser.lastname
            }
          }
        }
        self.doPost({
          userdata: self.options.user,
          data: payload
        }, self.currentFormUser ? 'updateUserById' : 'createUser', function (res) {
          if (res.data.status === 'error') {
            self.showRequestError(res);
          } else {
            // Rename the user's trips only once the user update itself succeeded
            if (tripsData) {
              self.doPost({data: tripsData}, 'updateTripsForUser', function (tripsRes) {
                if (tripsRes.data.status === 'error') {
                  self.showRequestError(tripsRes);
                }
              })
            }
            if (self.options.user.id === self.form.id) {
              self.options.user.username = self.form.username;
              self.options.user.telefon = self.form.telefon;
              self.options.user.firstname = self.form.firstname;
              self.options.user.lastname = self.form.lastname;
              self.options.user.trips_isolated = self.form.trips_isolated === 1 ? 1 : 0;
              self.options.user.pic = self.form.pic;
              self.options.user.role = self.form.role;
              self.options.user.company = self.form.company;
              self.options.user.brandings = self.form.brandings
            }
            let result = res.data.data
            if (result == "update") {
              self.setPdfStatus(self.form)
            }
            self.options.showSuccess = true;
            self.$emit('updateData', {});
            self.closeDialog();
          }
          self.sending = false;
        });
      }, 1000);
    });
  },
  async setPdfStatus(form){
		const self = this;
		let data = {
			tripId : "all",
			status : "false",
      firstname : form.firstname,
      lastname : form.lastname
		}
		await this.doPost({ data: data }, 'setStatusPdf', function (res) {
				if (res.data.status === 'error') {
				self.showRequestError(res);
				} else {
				let result = res.data.data
				}
			});
    },
    openEdit() {
      this.options.userFormEditable = true;
    },
    toggleEmailLock() {
      if (this.sending || !this.options.userFormEditable) return;
      if (this.emailLocked) {
        this.emailLocked = false;
        this.$nextTick(() => {
          // md-dialog is portaled to <body>, so look the input up globally
          const input = document.getElementById('user_email');
          if (input) input.focus();
        });
      } else {
        // Re-locking discards an unsaved e-mail change
        this.form.username = this.currentFormUser ? this.currentFormUser.username : this.form.username;
        this.emailLocked = true;
      }
    },
    showRequestError(res) {
      const key = res && res.msg;
      if (key === 'user_field_too_long') {
        const labels = {
          username: 'E-Mail',
          firstname: this.options.Lang.Vorname,
          lastname: this.options.Lang.Nachname,
          company: this.options.Lang.Firma,
          telefon: this.options.Lang.Telefon,
          pic: this.options.Lang.Account_Logo
        };
        // Fall back to the raw column name rather than leaving the "{field}"
        // placeholder in the message when the backend names an unmapped column.
        const field = (res.data && (labels[res.data.field] || res.data.field)) || null;
        if (field) {
          this.showErrorMsg(this.options.Lang.user_field_too_long.replace('{field}', field));
          return;
        }
      }
      this.showErrorMsg((key && this.options.Lang[key]) || this.options.Lang.user_request_failed);
    },
    openDialog() {

    },
    closeDialog() {
      this.clearForm();
      this.options.showUserForm = false;
      this.options.currentFormUser = null;
      this.options.userFormEditable = true;
    },
    validate() {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        this.save()
      }
    },
    async getBrandings(){
      // this.renderComp = false;
      let self = this;
      self.brands = []
      await self.doPost({}, 'getAllBranding', function (res) {
        if (res.data.status === 'error') {
          self.showRequestError(res);
        } else {
          let result = res.data.data;
          self.brands = [...result]
          result.forEach(brand => {
            // Gurad the app from showing error if brandingsToForm is null
            if (self.brandingsToForm && self.brandingsToForm.includes) {
              self.brandingsAllowed[brand.id] = self.brandingsToForm.includes(brand.id);
            }
          });
        }
      });
    },
    toggleBrand(brandId) {
      this.brandingsToForm = []
      Object.entries(this.brandingsAllowed).forEach(brand=>{
        if (brand[1]) {
          this.brandingsToForm.push(brand[0]*1)
        }
        this.brandingsAllowed = {}
        this.brands.forEach(brand=>{
            this.brandingsAllowed[brand.id] = this.brandingsToForm.includes(brand.id)
          
          })
      this.form.brandings = JSON.stringify(this.brandingsToForm)
        
      })
      this.brandingsAllowed = this.brandingsAllowed
    }
  

  },
}
</script>
<style>
#firstname,#lastname,#company,#telefon,#passwordRepeat,#password,#user_email,#impressum,#datenschutz,#color{
  -webkit-text-fill-color: black !important;
}

.width100 {
  width: 100%;
}

.md-field > .md-icon.email-lock-icon {
  cursor: pointer;
  align-self: center;
  margin: 0 0 0 8px !important;
}

/* Locked login e-mail: greyed out (overrides the global .md-field input styles in app.css) */
.md-field.email-locked input.md-input#user_email {
  background-color: #E9EAEE !important;
  color: #9E9E9E !important;
  -webkit-text-fill-color: #9E9E9E !important;
  cursor: not-allowed;
}

.userButtonContainer {
  display: flex;
  width: 100%;
  justify-content: space-between;
}

.dFlexCen{
  display: flex;
  align-items: center;
}

.dFlexEnd {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
}

.pRight24 {
  padding-right: 24px !important;
}

.form-user-loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.form-user-spinner {
  border: 8px solid #f3f3f3;
  border-top: 8px solid #3498db;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
.password-change-dialog .md-dialog-container {
  min-width: 400px;
  max-width: 500px;
}
.confirm-2fa-dialog {
  max-width: 400px;
  text-align: center;
  margin: auto;
}
.confirm-2fa-title {
  text-align: center;
  font-weight: bold;
  padding: 24px 24px 0 !important;
}
.confirm-2fa-content {
  text-align: center;
  font-size: 1rem;
  padding: 24px 40px !important;
}
.confirm-2fa-actions {
  justify-content: center;
}
.confirm-2fa-actions .md-button {
  margin-left: 30px;
  margin-right: 30px;
}
</style>